import React, { useMemo, useState, FC, useEffect } from 'react'
import { Chip } from '@components/std-components/Chip'
import { ListItemElement } from '@components/std-components/SelectComponent'
import { SelectPopup, SelectPopupProps } from '@components/std-components/SelectPopup'
import Typography from '@components/std-components/Typography'
import ColorCircle from '@components/UI-Components/ChipList/ColorCircle'
import { AddIcon, CrossIcon } from '@components/UI-Components/svg'
import difference from 'lodash/difference'
import ChipList from '@components/UI-Components/ChipSelect/components/ChipList/ChipList'
import { SelectListItemColumn } from '@components/UI-Components/ChipSelect/components/SelectListItem'
import * as css from './RMClusterChip.css'
import classNames from 'classnames'

export interface ChipListProps extends Omit<SelectPopupProps, 'trigger'> {}

export const RMClusterChip: ({ colorMap }) => FC<ListItemElement> =
  ({ colorMap }) =>
  ({ isSelected, onDeselect, onSelect, option, disabled, index }) => {
    return (
      <Chip
        tooltip={option}
        key={option}
        offset={{ bottom: 12 }}
        className={classNames(css['chip'], { disabled: disabled })}
      >
        <div className={css['color-item']}>
          <ColorCircle color={colorMap[option]} />
        </div>
        <Typography
          variant='body2'
          color='primary'
          classes={classNames(css['content'], 'text-ellipsis')}
        >
          {option}
        </Typography>
        {!disabled && (
          <span className={classNames('flex', css['chip-action'])}>
            {isSelected ? (
              <img
                src={CrossIcon}
                className='icon-16'
                alt={'Remove'}
                onClick={() => onDeselect()}
              />
            ) : (
              <img
                src={AddIcon}
                className='icon-16'
                alt={'Add'}
                onClick={() => onSelect()}
              />
            )}
          </span>
        )}
      </Chip>
    )
  }

export const ChipSelect: FC<ChipListProps> = ({ ...props }) => {
  const [selected, setSelected] = useState(props.selected)
  useEffect(() => {
    setSelected(props.selected)
  }, [props.selected])
  const options = useMemo(
    () => difference(props.options, selected),
    [props.options, selected]
  )

  return (
    <SelectPopup
      trigger={
        <>
          <div
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <ChipList
              itemTemplate={props.itemTemplate}
              onDeselect={option => {
                const newSelection = difference(selected, [option])
                setSelected(newSelection)
                props.onSelect({ selected: newSelection })
              }}
              values={selected}
              disabled={props.disabled}
            />
          </div>
          {!props.disabled && options?.length > 0 && (
            <div>
              <img src={AddIcon} alt='Add' />
            </div>
          )}
        </>
      }
      {...props}
      closeOnSelect={true}
      onSelect={({ selected: currSelected }) => {
        setSelected(currSelected)
        props.onSelect({ selected: currSelected })
      }}
      contentStyle={{ width: 110, padding: 10 }}
      selected={selected}
      options={options}
      listElementTemplate={SelectListItemColumn}
      itemTemplate={props.itemTemplate}
    />
  )
}
